import React, { type ChangeEvent, type FormEvent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl, authFetch } from '../utils/api';
import { useUser } from '../contexts/UserContext';
import { 
  GenerateCodeFormDataNew, 
  Teacher, 
  Subject, 
  CourseNew, 
  TeacherResponse, 
  SubjectResponse, 
  CourseNewResponse 
} from '../types/unitCode';

interface GeneratedCode {
  code: string;
  grade: string;
  courseName: string;
}

const GenerateCode: React.FC = () => {
  const navigate = useNavigate();
  const { isTeacher, getTeacherId } = useUser();
  const [formData, setFormData] = useState<GenerateCodeFormDataNew>({
    teacherId: '',
    subjectId: '',
    term: '',
    courseId: '',
    numberOfCodes: 1,
  });

  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [courses, setCourses] = useState<CourseNew[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTeachers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(apiUrl('Teacher'));
      
      if (!response.ok) {
        throw new Error('Failed to fetch teachers');
      }
      
      const data: TeacherResponse = await response.json();
      if (data.success) {
        setTeachers(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch teachers');
      }
    } catch (err) {
      setError('فشل في تحميل المعلمين. يرجى المحاولة مرة أخرى.');
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async (teacherId: string) => {
    if (!teacherId) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(apiUrl(`Teacher/${teacherId}/subjects`));
      
      if (!response.ok) {
        throw new Error('Failed to fetch subjects');
      }
      
      const data: SubjectResponse = await response.json();
      if (data.success) {
        setSubjects(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch subjects');
      }
    } catch (err) {
      setError('فشل في تحميل المواد. يرجى المحاولة مرة أخرى.');
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async (teacherId: string, subjectId: string, term: string) => {
    if (!teacherId || !subjectId || !term) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(
        apiUrl(`Course/all?Term=${term}&SubjectId=${subjectId}&TeacherId=${teacherId}`),
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch courses');
      }
      
      const data: CourseNewResponse = await response.json();
      if (data.success) {
        setCourses(data.data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch courses');
      }
    } catch (err) {
      setError('فشل في تحميل الباقات. يرجى المحاولة مرة أخرى.');
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  // Auto-select teacher for teacher users
  useEffect(() => {
    if (isTeacher() && getTeacherId()) {
      setFormData(prev => ({
        ...prev,
        teacherId: getTeacherId()!
      }));
    }
  }, [isTeacher, getTeacherId]);

  useEffect(() => {
    if (formData.teacherId) {
      fetchSubjects(formData.teacherId);
    } else {
      setSubjects([]);
      setCourses([]);
    }
  }, [formData.teacherId]);

  useEffect(() => {
    if (formData.teacherId && formData.subjectId && formData.term) {
      fetchCourses(formData.teacherId, formData.subjectId, formData.term);
    } else {
      setCourses([]);
    }
  }, [formData.teacherId, formData.subjectId, formData.term]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: name === 'numberOfCodes' ? parseInt(value, 10) || 0 : value,
      };

      // Reset dependent fields when parent fields change
      if (name === 'teacherId') {
        newData.subjectId = '';
        newData.courseId = '';
        newData.term = '';
      } else if (name === 'subjectId') {
        newData.courseId = '';
        newData.term = '';
      } else if (name === 'term') {
        newData.courseId = '';
      }

      return newData;
    });
  };

  const downloadAsCSV = (codes: GeneratedCode[]) => {
    // Create CSV content with proper encoding for Arabic text
    const headers = ['Code', 'Grade', 'Course Name'];
    const rows = codes.map(code => [
      code.code,
      code.grade,
      `"${code.courseName.replace(/"/g, '""')}"` // Properly escape quotes and wrap in quotes for Arabic text
    ]);
    
    // Add BOM for proper UTF-8 encoding
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n'); // Using standard newline instead of escaped

    // Create blob with UTF-8 encoding
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Get the selected course name for the filename
    const selectedCourse = courses.find(course => course.id === formData.courseId);
    const filename = selectedCourse 
      ? `${selectedCourse.courseName}-codes-${new Date().toISOString().split('T')[0]}.csv`
      : `course-codes-${new Date().toISOString().split('T')[0]}.csv`;
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Make API call to generate codes with POST method
      const response = await authFetch(
        apiUrl(`CourseCode/GenerateCodeCourse?Courseid=${formData.courseId}&Count=${formData.numberOfCodes}`),
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate codes');
      }

      const result = await response.json();
      
      if (result.success) {
        // Download the generated codes as CSV
        downloadAsCSV(result.data);
        alert(result.message || 'تم إنشاء الرموز وتحميلها بنجاح!');
        navigate('/codes');
      } else {
        throw new Error(result.message || 'فشل في إنشاء الرموز');
      }
    } catch (err) {
      setError('فشل في إنشاء الرموز. يرجى المحاولة مرة أخرى.');
      console.error('Error generating codes:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6" dir="rtl">
      <h1 className="text-2xl font-bold mb-6">إنشاء رموز الدورة</h1>

      <div className="bg-white p-6 rounded-lg shadow max-w-2xl mx-auto">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                المعلم
              </label>
              <select
                name="teacherId"
                value={formData.teacherId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                disabled={loading || isTeacher()}
              >
                <option value="">اختر المعلم</option>
                {teachers.map((teacher: Teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.fullName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                المادة
              </label>
              <select
                name="subjectId"
                value={formData.subjectId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                disabled={!formData.teacherId || loading}
              >
                <option value="">
                  {loading 
                    ? 'جاري تحميل المواد...' 
                    : error 
                    ? 'خطأ في تحميل المواد' 
                    : 'اختر المادة'
                  }
                </option>
                {subjects.map((subject: Subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الفصل الدراسي
              </label>
              <select
                name="term"
                value={formData.term}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                disabled={!formData.subjectId || loading}
              >
                <option value="">اختر الفصل الدراسي</option>
                <option value="First">الفصل الأول</option>
                <option value="Second">الفصل الثاني</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الدورة
              </label>
              <select
                name="courseId"
                value={formData.courseId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                disabled={!formData.teacherId || !formData.subjectId || !formData.term || loading}
              >
                <option value="">
                  {loading 
                    ? 'جاري تحميل الباقات...' 
                    : error 
                    ? 'خطأ في تحميل الباقات' 
                    : 'اختر الدورة'
                  }
                </option>
                {courses.map((course: CourseNew) => (
                  <option key={course.id} value={course.id}>
                    {course.courseName}
                  </option>
                ))}
              </select>
              {error && (
                <p className="mt-1 text-sm text-red-600">{error}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                عدد الرموز
              </label>
              <input
                type="number"
                name="numberOfCodes"
                value={formData.numberOfCodes}
                onChange={handleInputChange}
                min="1"
                max="501"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                disabled={loading}
              />
            </div>

            <div className="flex justify-end space-x-reverse space-x-4">
              <button
                type="button"
                onClick={() => navigate('/codes')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 bg-primary text-white rounded-lg transition-colors ${
                  loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/90'
                }`}
              >
                {loading ? 'جاري الإنشاء...' : 'إنشاء'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GenerateCode; 